import * as dayjs from 'dayjs';
import { AnyHash, nString, uNumber } from '@hidat/huijs-common';
import { hasResourceType, PlayerMixInterface, SplixerResourceTypes } from '@splixer/common';
import { dayjsTransformer, DayJsUtils } from '@hidat/huijs-dates';
import { PlayerProgramBase } from './program-base.model';
import { PlayerShowBase } from './show-base.model';
import { PlayerExtendedBranding } from './extended-branding.model';
import { PlayerHostBase } from './host-base.model';
import { Expose, Transform, Type } from 'class-transformer';

/**
 * Utility class that contains information about when a mix was played
 */
export class PlayedAtInfo {
  @Expose({ name: 'session_path_id' })
  sessionPathId: number | undefined;
  @Expose({ name: 'created_at' })
  @Transform(dayjsTransformer, { toClassOnly: true })
  createdAt: dayjs.Dayjs | undefined;
  @Expose({ name: 'duration' })
  duration: number | undefined;
  @Expose({ name: 'time_spent' })
  timeSpent: number | undefined;
}

/**
 *
 * This is a subset/denormalization of the content path, as used in the player.
 */
export class PlayerMix implements PlayerMixInterface, hasResourceType {
  resourceType = SplixerResourceTypes.Mix;

  @Expose({ name: 'id' })
  id = -1;
  @Expose({ name: 'uid' })
  uid: string | undefined = undefined;

  @Expose({ name: 'name' })
  name: string | undefined = undefined;
  @Expose({ name: 'description' })
  description: string | undefined = undefined;
  @Expose({ name: 'tag_line' })
  tagLine: string | undefined = undefined;
  @Expose({ name: 'steerable_tag_line' })
  steerableTagLine: string | undefined = undefined;
  @Expose({ name: 'image_url' })
  imageUrl: string | undefined = undefined;
  @Expose({ name: 'short_code' })
  shortCode: string | undefined = undefined;
  @Expose({ name: 'branding' })
  @Type(() => PlayerExtendedBranding)
  branding: PlayerExtendedBranding = {};

  @Expose({ name: 'category_code' })
  categoryCode: string | undefined = undefined;
  @Expose({ name: 'media_type' })
  mediaType: string | undefined = undefined;
  @Expose({ name: 'featured' })
  featured = false;
  @Expose({ name: 'has_playlist' })
  hasPlaylist = true;
  @Expose({ name: 'display_show' })
  displayShow = true;
  @Expose({ name: 'display_host' })
  displayHost = true;
  @Expose({ name: 'priority' })
  priority: uNumber = undefined;
  @Expose({ name: 'url_stub' })
  urlStub: nString;

  @Expose({ name: 'program_id' })
  programId: number | undefined = undefined;
  @Expose({ name: 'host_id' })
  hostId: number | undefined = undefined;
  @Expose({ name: 'host_name' })
  hostName: string | undefined = undefined;
  @Expose({ name: 'source_type' })
  sourceType: string | undefined = undefined;
  @Expose({ name: 'source_uid' })
  sourceUid: string | undefined = undefined;
  @Expose({ name: 'broadcasted_at' })
  @Transform(dayjsTransformer, { toClassOnly: true })
  broadcastedAt: dayjs.Dayjs | undefined = undefined;
  @Expose({ name: 'is_steerable' })
  isSteerable = true;
  @Expose({ name: 'is_selectable' })
  isSelectable = true;

  @Expose({ name: 'filter_config' })
  filterConfig: AnyHash = {};
  @Expose({ name: 'filter_klass' })
  filterKlass: string | undefined = undefined;
  @Expose({ name: 'valid_segments' })
  validSegments = 0;
  @Expose({ name: 'duration' })
  duration = 0;

  @Expose({ name: 'mic_break_mode' })
  micBreakMode: number | undefined = undefined;
  @Expose({ name: 'min_duration_hours' })
  minDurationHours: number | undefined = undefined;
  @Expose({ name: 'playback_type' })
  playbackType: string | undefined = undefined;
  @Expose({ name: 'scheduling_klass' })
  schedulingKlass: string | undefined = undefined;

  @Expose({ name: 'station_id' })
  stationId: number | undefined = undefined;
  @Expose({ name: 'status' })
  status = 100;

  @Expose({ name: 'created_at' })
  @Transform(dayjsTransformer, { toClassOnly: true })
  createdAt: dayjs.Dayjs | undefined = undefined;
  @Expose({ name: 'updated_at' })
  @Transform(dayjsTransformer, { toClassOnly: true })
  updatedAt: dayjs.Dayjs | undefined = undefined;

  @Expose({ name: '_show' })
  @Type(() => PlayerShowBase)
  _show: PlayerShowBase | undefined = undefined;
  @Expose({ name: '_host' }) //, PlayerHostBase, true)
  @Type(() => PlayerHostBase)
  _host: PlayerHostBase | undefined = undefined;
  @Expose({ name: '_source' }) //, Object, true)
  _source: AnyHash = {};
  @Expose({ name: '_program' })
  @Type(() => PlayerProgramBase)
  _program: PlayerProgramBase | undefined = undefined;
  @Expose({ name: '_related_content' })
  @Type(() => PlayerMix)
  _relatedContent: PlayerMix[] | undefined = undefined;
  @Expose({ name: '_played_at' })
  @Type(() => PlayedAtInfo)
  _playedAt: PlayedAtInfo | undefined;
}

/********** Interfaces to send data to API ****************************************************************************/
//@TODO  This needs a bunch of cleaning up once I understand it again....
export class SessionFilter {
  //? hosts: any;
  //? programs: any;
  startTime: number | undefined; // 24 hour clock
  endTime: number | undefined;
  hostIds: string[] | undefined;
  programIds: string[] | undefined;
  dow: number[] | undefined;
  startDate: dayjs.Dayjs | undefined;
  endDate: dayjs.Dayjs | undefined;

  constructor(hash?: any) {
    if (hash) {
      if (hash.dow) {
        this.dow = hash.dow;
      }
      if (hash.hostIds) {
        this.hostIds = hash.hostIds;
      } else if (hash.host_id) {
        this.hostIds = hash.host_id;
      }
      if (hash.programIds) {
        this.programIds = hash.programIds;
      } else if (hash.program_id) {
        this.programIds = hash.program_id;
      }
      if (hash.startTime) {
        this.startTime = hash.startTime;
      } else if (hash.start_time) {
        this.startTime = hash.start_time;
      }
      if (hash.startDate) {
        this.startDate = hash.startDate;
      } else if (hash.start_date) {
        this.startDate = DayJsUtils.toDayjs(hash.start_date);
      }
      if (hash.endTime) {
        this.endTime = hash.endTime;
      } else if (hash.end_time) {
        this.endTime = hash.end_time;
      }
      if (hash.endDate) {
        this.endDate = hash.endDate;
      } else if (hash.end_date) {
        this.endDate = DayJsUtils.toDayjs(hash.end_date);
      }
    }
  }
}

export class UserSettings {
  public includeMicBreaks: boolean;

  public constructor(apiHash?: AnyHash) {
    if (apiHash) {
      this.includeMicBreaks = apiHash['include_mic_breaks'];
    } else {
      this.includeMicBreaks = true;
    }
  }
}

/**
 * Mix Configuration
 * Includes the current set of filters, as well as the mic break setting and anything else we can dream up.
 */
export class MixConfiguration {
  public userFilters: SessionFilter;
  public userSettings: UserSettings;

  public constructor(apiHash?: AnyHash) {
    if (apiHash) {
      this.userFilters = new SessionFilter(apiHash['user_filters']);
      this.userSettings = new UserSettings(apiHash['user_settings']);
    } else {
      this.userFilters = new SessionFilter();
      this.userSettings = new UserSettings();
    }
  }
}
